import React from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import type { SeActivityNode } from '../../../hooks/useSfTeamActivitySummary';
import {
  ACTIVITY_CATEGORIES,
  activityByReportRows,
  teamActivityTotals,
} from '../../../support/salesforceTeamActivity';
import CampaignChartCard, { ChartGrid, KpiCards } from '../Campaigns/CampaignChartCard';

interface Props {
  /** Activity summary tree rooted at the selected leader; null until loaded. */
  tree: SeActivityNode | null;
  loading: boolean;
  error?: string | null;
  /** The time period in words (e.g. "This Fiscal Quarter"). */
  periodLabel: string;
}

/**
 * Team Summary Dashboard: the selected leader's team total for every activity type, then one chart per type
 * showing how it splits across their direct reports. Every number is a `rollup` (the person plus everyone
 * under them), so a manager is never shown as just their own activities.
 */
export default function SalesforceActivityDashboard({ tree, loading, error, periodLabel }: Props) {
  if (loading) return <HbrSpinner>Loading activity…</HbrSpinner>;
  if (error) return <div style={{ padding: 12, color: 'red' }}>Activity data is unavailable: {error}</div>;

  const totals = teamActivityTotals(tree);
  if (!tree || !totals) {
    return <div className="hbr-css__text-secondary-sm">No activity data is available.</div>;
  }

  const owner = tree.email.toLowerCase();
  return (
    <div>
      <KpiCards kpis={ACTIVITY_CATEGORIES.map((c) => ({ label: c, value: totals[c] }))} />
      <ChartGrid>
        {ACTIVITY_CATEGORIES.map((category) => (
          <CampaignChartCard
            key={category}
            title={`${category} by report`}
            subtitle={`${periodLabel} — team totals, sub-teams included`}
            chartType="donut_chart"
            data={activityByReportRows(tree, category)}
            tileKey={`sf-activity-${owner}-${category}`}
            tooltipKeys={{ label: 'Report', value: category }}
            empty={`No ${category} activity in this period.`}
          />
        ))}
      </ChartGrid>
    </div>
  );
}
